"use client";

import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { FieldValues } from "../../components/DynamicForm";
import FormSteps, {
  FormStep,
  NextStep,
  StepName,
} from "../../components/FormSteps";
import { FathomEvent } from "../constants";
import { FormState } from "../context/BudgetCheckContext";
import { fathom } from "@justhome/common";

const isNeedToUpdateDefaultValue = (step: any) => {
  return step?.config?.fields?.some((field: any) => {
    return typeof field?.defaultValue === "function";
  });
};

const updateWithDefaultValue = async <T extends FieldValues>(
  step: FormStep<T>,
  value: any,
) => {
  if (!step.config.fields) {
    return;
  }

  await Promise.all(
    step.config.fields.map(async (field) => {
      if (typeof field?.defaultValue === "function" && !value[field.name]) {
        value[field.name] = await field.defaultValue(value);
      }
    }),
  );
};

interface UseFormSteps<T extends FieldValues> {
  formSteps: FormSteps<T>;
  hasZeroStep: boolean;
  formState: FormState<T>;
  onSetForm: (formState: FormState<T>) => void;
  onSubmit?: (form: T) => void;
}

const useFormSteps = <T extends FieldValues>({
  formSteps,
  hasZeroStep,
  formState,
  onSetForm,
  onSubmit,
}: UseFormSteps<T>) => {
  const router = useRouter();

  const currentStepNumber = formState.history.length;

  const value: T = useMemo(
    () =>
      formState.values.reduce(
        (prev, value) => ({ ...prev, ...value }),
        {},
      ) as T,
    [formState.values],
  );

  const nextStep = async (value: any, stepName?: StepName) => {
    if (typeof formState.currentStep !== "number") {
      return;
    }

    const currentStep = formSteps.steps[formState.currentStep];

    if (!currentStep) return;

    const nextStepName =
      stepName ??
      (typeof currentStep.transition === "function"
        ? currentStep.transition(value)
        : currentStep.transition);

    const nextStepIndex = formSteps.steps.findIndex(
      (step) => step.name === nextStepName,
    );

    const stepIndex = formState.history.length;
    if (stepIndex === 0) {
      fathom.trackEvent(FathomEvent.APPLICATION_STARTED);
    }

    const nextStep = formSteps.steps[nextStepIndex];

    // TODO handle fast clicking on button -> pushing the same step multiple times
    if (nextStep) {
      if (isNeedToUpdateDefaultValue(nextStep)) {
        await updateWithDefaultValue(nextStep, value);
      }

      if (nextStep.name === currentStep.name) {
        throw new Error(
          `Potential loop detected, cannot transition from step ${nextStep.name} to ${currentStep.name}`,
        );
      }

      const values = [...formState.values, value];

      if (nextStep.navigateTo) {
        onSubmit?.(value);
        router.push(nextStep.navigateTo());

        // We do not need to update the formState if there is no value.
        // This prevents unnecessary executions of useEffects that have the state as a dependency.
        if (value) {
          onSetForm({
            ...formState,
            values,
          });
        } else {
          onSetForm(formState);
        }
      } else {
        const form = {
          values: values,
          history: [...formState.history, formState.currentStep],
          currentStep: nextStepIndex,
        };
        onSetForm(form);
      }
    } else {
      throw new Error(`Step with name ${nextStepName} does not exist.`);
    }
  };

  const goBackToStep = (stepName: StepName) => {
    const index = formSteps.steps.findIndex((step) => step.name === stepName);
    const historyIndex = formState.history.indexOf(index);
    if (index >= 0) {
      onSetForm({
        ...formState,
        currentStep: index,
        history: formState.history.slice(0, historyIndex),
      });
    }
  };

  const previousStep = () => {
    if (formState.history.length) {
      onSetForm({
        ...formState,
        values: formState.history.length === 1 ? [] : formState.values,
        currentStep: formState.history[formState.history.length - 1] ?? 0,
        history: formState.history.slice(0, -1),
      });
    }
  };

  const nextSteps = useMemo(() => {
    const currentStep = formSteps.steps[formState.currentStep];

    if (!currentStep) return [];

    const stepsToVisit: FormStep<T>[] = [currentStep];

    const nextSteps: NextStep[] = formState.history.map((i) => {
      const step = formSteps.steps[i];
      if (!step) return {} as NextStep;

      return {
        label: step.label,
        completed: step !== currentStep,
        name: step.name,
        config: step.config,
        composite: step.composite,
      };
    });

    while (stepsToVisit.length) {
      const step = stepsToVisit.pop() as FormStep<T>;

      if (step.hidden) {
        continue;
      }

      if (!nextSteps.some((s) => s.name === step.name)) {
        nextSteps.push({
          label: step.label,
          name: step.name,
          config: step.config,
          completed: false,
          composite: step.composite,
        });
      }

      if (step.isResult) {
        continue;
      }

      if (step.possibleTransitions) {
        for (const nextStepName of step.possibleTransitions) {
          const nextStep = formSteps.steps.find(
            (step) => step.name === nextStepName,
          );

          if (nextStep) {
            stepsToVisit.push(nextStep);
          }
        }
      } else {
        const nextStepName = step.transition;
        const nextStep = formSteps.steps.find(
          (step) => step.name === nextStepName,
        );

        if (nextStep) {
          stepsToVisit.push(nextStep);
        }
      }
    }
    if (hasZeroStep) {
      return nextSteps.slice(1);
    }
    return nextSteps;
  }, [hasZeroStep, formSteps.steps, formState.history, formState.currentStep]);

  const numberOfSteps = useMemo(() => {
    const currentStep = formSteps.steps[formState.currentStep];
    if (!currentStep) return 0;

    const stepsToVisit: {
      step: FormStep<T>;
      depth: number;
    }[] = [
      {
        step: currentStep,
        depth: currentStepNumber,
      },
    ];
    const visitedSteps: FormStep<T>[] = [];

    let maxDepth = currentStepNumber;

    while (stepsToVisit.length) {
      const step = stepsToVisit.pop() as {
        step: FormStep<T>;
        depth: number;
      };
      visitedSteps.push(step.step);
      maxDepth = Math.max(step.depth, maxDepth);

      if (step.step.isResult) {
        continue;
      }

      if (typeof step.step.transition !== "function") {
        const nextStepName = step.step.transition;
        const nextStep = formSteps.steps.find(
          (step: any) => step.name === nextStepName,
        );

        if (nextStep) {
          stepsToVisit.push({
            step: nextStep,
            depth: step.depth + 1,
          });
        }
      } else if (step.step.possibleTransitions) {
        for (const nextStepName of step.step.possibleTransitions) {
          const nextStep = formSteps.steps.find(
            (step: any) => step.name === nextStepName,
          );

          if (nextStep) {
            stepsToVisit.push({ step: nextStep, depth: step.depth + 1 });
          }
        }
      }
    }
    return maxDepth;
  }, [formSteps.steps, formState.currentStep, currentStepNumber]);

  return {
    currentStep: formSteps.steps[formState.currentStep],
    value,
    numberOfSteps,
    currentStepNumber,
    previousStep,
    nextStep,
    nextSteps,
    goBackToStep,
  };
};

export default useFormSteps;
